import React, { useState } from "react";
import { DiningOption, UniqueShip } from "@/interfaces/IUniqueShip";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItemButton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowNextIcon from "@mui/icons-material/ArrowForwardRounded";

import { Contents, CustomNestedButtons } from "./CruiseFacilities";
import { toKebabCase, toTitleCase, toTitleCaseV2 } from "@/utils/helpers";
import { SwiperComponent } from "@/components/common/SwiperComponent";

const CruiseDining: React.FC<{
  shipData?: UniqueShip;
}> = ({ shipData }) => {
  const isMobile = useMediaQuery("(max-width: 900px)");

  const diningExperience = shipData?.diningOptions
    ?.filter(
      (item, index, self) => index === self?.findIndex((x) => x?.experience === item?.experience),
    )
    .map((x) => x?.experience);

  return (
    <>
      <>
        {isMobile ? (
          <>
            <CruiseDiningMobile diningExperience={diningExperience} shipData={shipData} />
          </>
        ) : (
          <>
            {(diningExperience?.length ?? 0) > 0 && (
              <Box
                sx={{
                  display: { xs: "none", md: "inline-block" },
                  // float: "inline-end",
                  position: "relative",
                  mt: "1.5rem",
                  width: "100%",
                  ".MuiAccordion-gutters": {
                    boxShadow:
                      "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
                    border: "2px solid #060273",
                    borderBottom: "5px solid #060273",
                    borderRadius: "15px !important",
                    mb: "1rem",
                  },
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    top: "0px",
                    left: "-24px",
                    height: "100%",
                    width: "calc(100% + 48px)",
                    bgcolor: "#f0efff", //"#f0f0f0",
                    zIndex: -1,
                    borderRadius: { xs: "0px", xl: "24px" },
                  },
                }}
              >
                <CruiseDiningTypes shipData={shipData} formattedDiningOptions={diningExperience} />
              </Box>
            )}
          </>
        )}
      </>
    </>
  );
};

export default CruiseDining;

const CruiseDiningTypes: React.FC<{
  shipData?: UniqueShip;
  formattedDiningOptions?: (string | null | undefined)[];
}> = ({ shipData, formattedDiningOptions }) => {
  const [isReadMore, setIsReadMore] = useState(false);
  const [sectionID, setSectionID] = useState("");

  const [selectedDining, setSelectedDining] = useState(
    shipData?.diningOptions?.filter((item) => {
      return item.experience == formattedDiningOptions?.[0];
    })?.[0],
  );

  return (
    <>
      <Grid container size={{ xs: 12 }} sx={{ mt: "2.5rem", mb: "1rem" }} spacing={2}>
        {formattedDiningOptions && formattedDiningOptions?.length > 0 && (
          <Grid size={{ xs: 12, md: 3 }}>
            <Box
              sx={{
                position: "sticky",
                top: "185px",
              }}
            >
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontSize: { xs: "1.5rem", md: "16px" },
                  textTransform: "uppercase",
                  color: "#555",
                  textAlign: "left",
                  width: "180px",
                }}
              >
                Dining Options
              </Typography>

              <Box
                sx={{
                  position: "sticky",
                  top: "185px",
                  height: "fit-content",
                  maxHeight: "calc(100vh - 210px)",
                  overflow: "auto",
                }}
              >
                {formattedDiningOptions?.map((categoryItem, cIndex) => (
                  <CustomNestedButtons
                    key={cIndex}
                    label={toTitleCaseV2(categoryItem ?? "") ?? ""}
                    isDefaultOpen
                    isV2Design
                  >
                    <List component="div" disablePadding>
                      {shipData?.diningOptions
                        ?.filter((item) => toKebabCase(item?.experience ?? "") === categoryItem)
                        ?.map((dining, diningIndex) => (
                          <ListItemButton
                            key={diningIndex}
                            sx={{
                              mb: "8px",
                              display: "flex",
                              justifyContent: "space-between",
                              borderRadius: "8px",
                              fontFamily: "'blauerNue', 'blauerNue Fallback'",
                              p: "8px 10px 8px 16px",
                              bgcolor: "#fff",
                              color: "#1f1f1f",
                              border: "2px solid #cfcdff",
                              transition: "all 0.3s",
                              "&:hover": {
                                bgcolor: "#f3f3f3",
                                color: "#3000ff",
                                border: "2px solid #3000ff",
                              },
                              ...(selectedDining?.name === `${dining?.name}` && {
                                bgcolor: "#E30053",
                                color: "#fff",
                                border: "2px solid #E30053",
                                "&:hover": {
                                  bgcolor: "#E30053",
                                  color: "#fff",
                                  border: "2px solid #E30053",
                                },
                              }),
                            }}
                            onClick={() => {
                              setSelectedDining(dining);
                              setIsReadMore(false);
                            }}
                          >
                            <Box component="span">{dining?.name}</Box>
                            <Box
                              sx={{
                                display: "grid",
                                placeItems: "center",
                                color: "#fff",
                                borderRadius: "16px",
                                height: "30px",
                                svg: {
                                  width: "30px",
                                  height: "30px",
                                },
                              }}
                            >
                              {selectedDining?.name === `${dining?.name}` ? (
                                <ArrowNextIcon />
                              ) : (
                                <></>
                              )}
                            </Box>
                          </ListItemButton>
                        ))}
                    </List>
                  </CustomNestedButtons>
                ))}
              </Box>
            </Box>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 9 }} spacing={2}>
          <Box sx={{}} id={`${toKebabCase(selectedDining?.name ?? "")}`}>
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="h3"
                align="center"
                gutterBottom
                sx={{
                  fontSize: { xs: "1.1rem", md: "2rem" },
                  color: "#E30053",
                  textAlign: "left",
                  width: "100%",
                  mb: "10px",
                }}
              >
                {toTitleCase(selectedDining?.name ?? "")}
              </Typography>
              <Box sx={{ width: "100%", display: "block" }}>
                {(selectedDining?.images?.filter((x) => x.href != "").length ?? 0) > 0 && (
                  <Box
                    sx={{
                      width: { xs: "100%", md: "50%" },
                      float: { md: "left" },
                      mr: { md: "1.2rem" },
                      mb: { xs: "1rem", md: "1rem" },
                    }}
                  >
                    <SwiperComponent
                      images={selectedDining?.images
                        ?.filter((x) => x.href != "")
                        .map((x) => ({ ...x, type: "image" }))}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    position: "relative",
                  }}
                >
                  <Contents
                    data={selectedDining?.description ?? ""}
                    isReadMore={isReadMore}
                    sectionID={sectionID}
                    onClick={() => {
                      setIsReadMore((prev) => !prev);
                      setSectionID("dining");
                    }}
                    isTypesOfFacilities
                    setIsReadMore={setIsReadMore}
                    setSectionID={setSectionID}
                    paragraphID={toKebabCase(selectedDining?.name ?? "") ?? ""}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

const CruiseDiningMobile: React.FC<{
  diningExperience?: (string | null | undefined)[];
  shipData?: UniqueShip;
}> = ({ shipData, diningExperience }) => {
  const [selectedDining, setSelectedDining] = useState(diningExperience?.[0]);

  return (
    <>
      {(diningExperience?.length ?? 0) > 0 && (
        <Box sx={{ mt: "1.5rem", display: { xs: "block", md: "none" } }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontSize: { xs: "1.3rem" },
              color: "#E30053", // "#8F0034",
              textAlign: "left",
              mt: "30px",
              mb: "10px",
              textTransform: "uppercase",
            }}
          >
            Food Variety
          </Typography>

          <Box
            sx={{
              bgcolor: "#FFECF3",
              width: "calc(100% + 2rem)",
              ml: "-1rem",
              px: "1rem",
              position: "sticky",
              top: { xs: "77px", md: "87px" },
              zIndex: 1099,
            }}
          >
            <Tabs
              value={selectedDining}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              id="mui_tabs"
              aria-label="scrollable auto tabs example"
              sx={{
                transitions: "all 0.5s",
                alignItems: "center",
                ".MuiTabs-root": {
                  boxShadow: "0px 0px 10px 0px #06027377",
                  overflow: "hidden",
                  alignItems: "center",
                },
                ".MuiTabs-flexContainer": {
                  borderRadius: "10px",
                  gap: "9px",
                  p: "10px 0px",
                  transition: "all 0.5s",
                  justifyContent: { xs: "left", xl: "center" },
                  alignItems: "center",
                  ".MuiButtonBase-root.MuiTab-root": {
                    border: "2px solid #E30053",
                    textTransform: "initial",
                    borderRadius: "10px",
                    fontWeight: 700,
                    transitions: "all 0.5s",
                    bgcolor: "#fff",
                    color: "#E30053",
                    minWidth: "60px",
                    height: "48px",
                    fontFamily: "'blauerNue', 'blauerNue Fallback'",
                    fontSize: "15px",
                    boxShadow: "0px 0px 5px #E3005360",
                    p: { xs: "5px 15px", md: "2px 11px 0px" },
                    "&.Mui-selected": {
                      bgcolor: "#E30053",
                      color: "#fff",
                      "&:hover": {
                        bgcolor: "#E30053",
                        color: "#fff",
                      },
                    },
                    "&:hover": {
                      bgcolor: "#fff",
                      color: "#E30053",
                    },
                  },
                },
                ".MuiTabs-indicator": {
                  display: "none",
                },
                ".MuiTabs-scrollButtonsHideMobile": {
                  display: "flex",
                },
                ".MuiButtonBase-root.MuiTabScrollButton-root.MuiTabs-scrollButtons": {
                  bgcolor: "#E30053",
                  color: "#fff",
                  opacity: 1,
                  borderRadius: "10px",
                  width: "26px",
                  height: "48px",
                  transitions: "all 0.5s",
                  svg: {
                    width: "35px",
                    height: "35px",
                    p: "2px 0px 0px",
                  },
                  "&:nth-of-type(1)": {
                    borderRadius: "10px 0px 0px 10px",
                  },
                  "&:nth-of-type(4)": {
                    borderRadius: "0px 10px 10px 0px",
                  },
                  "&.Mui-disabled": {
                    opacity: 0,
                    width: "0px",
                  },
                  "&:hover": {
                    bgcolor: "#E30053",
                    color: "#fff",
                  },
                },
              }}
            >
              {diningExperience?.map((diningType, cabinIndex) => (
                <Tab
                  key={cabinIndex}
                  iconPosition="start"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    ...(selectedDining === diningType && {
                      bgcolor: "#E30053 !important",
                      color: "#fff !important",
                    }),
                  }}
                  value={diningType}
                  label={
                    <>
                      <Box
                        sx={{
                          textTransform: "initial",
                          fontWeight: 600,
                          fontSize: { xs: "15px", md: "15px" },
                        }}
                      >
                        {toTitleCaseV2(diningType ?? "")}
                      </Box>
                    </>
                  }
                  onClick={() => {
                    setSelectedDining(diningType);
                  }}
                />
              ))}
            </Tabs>
          </Box>

          <Box
            sx={{
              mt: "10px",
              mb: "8px",
              color: "#666",
              fontSize: "12px",
              textAlign: "center",
            }}
          >
            Expand to view full food details
          </Box>

          <Box sx={{ px: "0rem", borderRadius: "16px", position: "relative" }}>
            <Box sx={{ position: "relative", zIndex: 9 }}>
              {shipData?.diningOptions
                ?.filter((d) => d.experience == selectedDining)
                .map((dine, dineI) => (
                  <CruiseAccordionMobile key={dineI} dine={dine} />
                ))}
            </Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                top: "-1px",
                left: "-1px",
                zIndex: 10,
                clipPath:
                  'path("M20.4883 0.326172C9.44275 0.326172 0.488545 9.27972 0.488281 20.3252V0.326172H20.4883Z")',
              }}
            ></Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                top: "-1px",
                right: "0px",
                zIndex: 10,
                clipPath:
                  'path("M20.7422 20.3262C20.7422 9.28064 11.7877 0.326435 0.742188 0.326171L20.7422 0.326172L20.7422 20.3262Z")',
              }}
            ></Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                bottom: "0px",
                left: "-1px",
                zIndex: 10,
                clipPath:
                  'path("M0.48828 0.65332C0.488281 11.6989 9.44281 20.6531 20.4883 20.6533L0.488281 20.6533L0.48828 0.65332Z")',
              }}
            ></Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                bottom: "0px",
                right: "0px",
                zIndex: 10,
                clipPath:
                  'path("M0.742188 20.6523C11.7877 20.6523 20.7419 11.6988 20.7422 0.65332L20.7422 20.6523L0.742188 20.6523Z")',
              }}
            ></Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export const MobileDiningCard: React.FC<{
  dine: DiningOption;
}> = ({ dine }) => {
  const [isReadMore, setIsReadMore] = useState(false);

  return (
    <Box
      sx={{
        borderTop: "1px solid #554fff",
        bgcolor: "#030047",
        p: "16px",
        position: "relative",
        zIndex: 10,
      }}
    >
      <Box sx={{ width: "100%", display: "block" }}>
        <Box
          sx={{
            width: "100%",
            mb: "8px",
            position: "relative",
          }}
        >
          <SwiperComponent
            isItineraryMobile
            images={dine?.images?.filter((x) => x.href != "").map((x) => ({ ...x, type: "image" }))}
          />
        </Box>

        <Box sx={{ position: "relative" }}>
          <Contents
            data={dine?.description ?? ""}
            isReadMore={isReadMore}
            onClick={() => {
              setIsReadMore((prev) => !prev);
            }}
            isTypesOfFacilities
            setIsReadMore={setIsReadMore}
            paragraphID={toKebabCase(dine?.name ?? "") ?? ""}
            isItineraryMobile
            drawerTitle={dine.name ?? ""}
          />
        </Box>
      </Box>
    </Box>
  );
};

const CruiseAccordionMobile: React.FC<{
  dine: DiningOption;
}> = ({ dine }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Accordion
        expanded={expanded}
        disableGutters
        sx={{
          bgcolor: "#060273",
          color: "#fff",
          overflow: "unset",
          ".MuiAccordionSummary-expandIconWrapper": {
            bgcolor: "#fff",
            border: "2px solid #ffffffaa",
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            svg: {
              width: "22px",
              height: "22px",
              color: "primary.main",
            },
          },
          "&.MuiPaper-root.MuiAccordion-root::before": {
            bgcolor: "#fff",
            zIndex: 120,
          },
          "&.MuiPaper-root.MuiAccordion-root.Mui-expanded::before": {
            opacity: 1,
          },
          "&:first-of-type": {
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          },
          "&:last-of-type": {
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          },
          ".MuiAccordionDetails-root": {
            pt: "0px",
            px: "0px",
            pb: "0px",
            borderBottom: "2px solid #ffffffaa",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandIcon />}
          sx={{
            background: expanded
              ? "linear-gradient(90deg,rgba(89, 0, 255, 1) 0%, rgba(46, 0, 130, 1) 100%)"
              : "primary.main",
            position: "sticky",
            top: "145px",
            zIndex: 100,
          }}
          onClick={() => {
            setExpanded((prev) => !prev);
          }}
        >
          <Box
            sx={{
              color: expanded ? "#fff" : "#fff",
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontSize: "1.3rem",
              fontWeight: 600,
              position: "relative",
              zIndex: 10,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "2px 10px",
              width: "100%",
            }}
          >
            <Box>{dine?.name}</Box>

            <Box
              sx={{
                bgcolor: "#3933e6",
                color: "#fff",
                p: "4px 8px 4px",
                borderRadius: "14px",
                border: "1px solid #fff7",
                width: "max-content",
                minWidth: "max-content",
                fontSize: "14px",
                lineHeight: 1.2,
                mr: "10px",
                height: "fit-content",
              }}
            >
              # {dine?.food}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <MobileDiningCard dine={dine} />
        </AccordionDetails>
      </Accordion>
    </>
  );
};
